/**
 * TierChecker: "Which NetSuite edition and service tier would you need?"
 * Two inputs (full user licences, average monthly transaction lines) and two
 * deliberately separate answers, because Oracle uses the words Standard /
 * Premium / Enterprise for both edition families and service tiers and the two
 * are independent. Every limit on screen names its source: Oracle's NetSuite
 * Service Descriptions (effective 15 September 2026) and the NetSuite Help
 * Center. A third, static panel shows the country edition (Hong Kong runs on
 * International), sourced to the Help Center's NetSuite Editions Overview.
 * Inputs are published to the other pricing widgets via pricing-handoff.ts
 * once the reader interacts. No prices anywhere. Follows the house chart pattern in
 * ./multi-entity.tsx ('use client', no required props, optional compact mode,
 * scoped CSS injected once, PS Global logo in the header, source line in the
 * footer, verbatim source wording in tooltips).
 */
'use client';

import React, { useEffect, useMemo, useState } from 'react';
import data from '@/components/netsuite-pricing/data/tier-checker.json';
import { writeHandoff } from '@/components/charts/shared/pricing-handoff';

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/* ------------------------------- Strings ------------------------------- */
/* Every visible string lives here so the widget can be translated later.   */
const STR = {
  title: 'Which NetSuite edition and service tier would you need?',
  titleAccent: 'Two separate answers.',
  sub: 'Oracle uses the words Standard, Premium and Enterprise for both edition names and service tier names, and they are independent. Your user count decides the edition family. Users and transaction lines together decide the service tier. Every edition, including the Premium editions, starts on the Standard service tier.',
  logoAlt: 'PS Global',
  sourceTip: 'Oracle wording',
  srcSd: (page: number) => `NetSuite Service Descriptions, effective 15 September 2026, p.${page}`,
  srcHc: 'NetSuite Help Center',

  usersLabel: 'People who post transactions (full user licences)',
  usersHint: 'Count General Access users only. Employee Center, Vendor Center and Customer Center users do not count towards the tier limit.',
  usersHintTipLabel: 'Oracle wording',
  usersHintTip: 'Oracle lists Employee Center as an EXCLUDED USER TYPE, one of the "User types that are not included in Customer\u2019s total NetSuite Service Tier User count", alongside Vendor Center, Customer Center and Partner Center.',
  srcSdGlossary: 'NetSuite Service Descriptions, effective 15 September 2026, Glossary',
  usersUnit: 'users',
  linesLabel: 'Transaction lines per month',
  modeProduct: 'Documents times lines',
  modeDirect: 'Enter lines directly',
  docsLabel: 'Orders, invoices and bills per month',
  lpdLabel: 'Lines per document',
  productLabel: 'Lines per month',
  directLabel: 'Average monthly transaction lines',
  times: 'times',
  equals: 'equals',

  editionEyebrow: 'Edition family',
  editionBasis: 'From users only',
  tierEyebrow: 'Service tier',
  tierBasis: 'From users and lines together',

  edition: {
    starter: { name: 'Starter', detail: '5 full users included, up to 10' },
    standard: { name: 'Standard', detail: 'Up to 30 full users in most Standard editions' },
    premium_or_enterprise: { name: 'Premium or Enterprise', detail: 'Oracle publishes no user limit above 30' },
  },
  editionDistance: (n: number, next: string) =>
    n === 0
      ? `At the limit. One more full user requires a ${next} edition.`
      : `${n} more full ${n === 1 ? 'user' : 'users'} before a ${next} edition is required.`,
  editionNextStandard: 'Standard',
  editionNextPremium: 'Premium or Enterprise',
  editionOpen: 'There is no published user ceiling to measure against.',
  editionFootnote: 'Exceptions in the Service Descriptions: Agency Standard allows up to 50 full users; Media Standard and Publishing Standard state no user cap. Enterprise editions are not described.',
  editionTipStarter: 'Starter editions include 5 General Access users and allow a maximum of 10. Beyond 10, Oracle requires an upgrade to a Standard, Premium or Enterprise edition.',
  editionTipStandard: 'Most Standard editions allow a maximum of 30 General Access users. Beyond 30, Oracle requires an upgrade to a Premium or Enterprise edition.',
  editionTipPremium: 'Oracle publishes no user limit for Premium editions and does not describe Enterprise editions.',

  tierDetail: (users: string, lines: string) => `Up to ${users} full users and ${lines} average monthly transaction lines`,
  tierSuffix: 'tier',
  tierDistance: (users: number, lines: string, limitName: string) =>
    `Room left before ${limitName}: ${users === 0 ? 'no more full users' : `${users.toLocaleString('en-US')} more full ${users === 1 ? 'user' : 'users'}`} and ${lines} more monthly lines.`,
  tierNextName: (tier: string) => `the ${tier} tier`,
  tierTopName: 'the Ultimate limits',
  badgeAria: (tier: string) => `Live result: ${tier}`,
  aboveUltimate: 'Above the Ultimate tier',
  aboveUltimateDetail: 'Your numbers exceed every published tier. Oracle says to contact a NetSuite sales representative to discuss options.',
  measuredAgainst: (tier: string) => `Bars measured against the ${tier} tier limits.`,
  metricUsers: 'Full users',
  metricLines: 'Monthly lines',
  ofLimit: (value: string, limit: string) => `${value} of ${limit}`,
  status: { under: 'Under limit', near: 'Near limit', over: 'Over limit' },
  binding: {
    users: 'Users are the binding constraint.',
    lines: 'Lines are the binding constraint.',
    tie: 'Users and lines sit at the same share of the limit.',
  },
  ruledOut: (tier: string, by: string, limit: string) => `The ${tier} tier is ruled out because ${by} exceed its ${limit}.`,
  byUsers: 'users',
  byLines: 'lines',
  byBoth: 'users and lines',
  limitUsers: (n: string) => `limit of ${n} full users`,
  limitLines: (n: string) => `limit of ${n} lines`,
  limitBoth: (u: string, l: string) => `limits of ${u} full users and ${l} lines`,
  tierUpgradeNote: 'Exceeding a limit means Oracle requires an upgrade to the applicable tier, and there is no downgrade during the term.',

  countryEyebrow: 'Country edition',
  countryBasis: 'A third meaning of edition',
  countryName: 'International (XX)',
  countryLead: 'Hong Kong companies run on NetSuite\u2019s International edition, because Oracle has no Hong Kong localised edition. Singapore and Mainland China aren\u2019t localised either.',
  countryLocalised: (list: string) => `Localised editions: ${list}.`,
  countryTax: 'International has no precoded tax, which suits Hong Kong: no VAT or GST.',
  countryOneWorld: 'In OneWorld, each subsidiary\u2019s address country sets its edition.',
  countryNotTier: 'Not related to Standard or Premium. It applies whatever your edition family and tier.',
  countryTip: 'This edition is not country-specific and is used for countries that do not have a localized edition.',
  countrySrcLabel: 'Source:',
  countrySrc: 'NetSuite Editions Overview, NetSuite Help Center',

  helpersTitle: 'Before you trust the number',
  helpers: {
    counting: 'Oracle counts every line item, and components count too: one assembly with five components on a sales order is seven lines, not two.',
    average: 'The figure Oracle measures is a six-month rolling average of lines created per month across all transaction types.',
    noDowngrade: 'Once you exceed a limit Oracle requires an upgrade, and you cannot move to a lower service tier during the term.',
    storage: 'File Cabinet storage and SuiteCloud Plus licences have tier limits too, which this tool does not ask about; storage alone gets 90 calendar days to come back under.',
    dashboard: 'Administrators can see the real numbers at Setup > Company > Service Tier Dashboard.',
  },
  countingTip: 'Oracle measures a six-month rolling average across all transaction types, and component lines of assemblies, kits and groups count (1 assembly with 5 components on a sales order = 7 lines, not 2).',
  dashboardTip: 'Setup > Company > Service Tier Dashboard, and Setup > Company > View Billing Information.',

  footer: 'Limits from Oracle\u2019s NetSuite Service Descriptions (effective 15 September 2026) and the NetSuite Help Center. No prices.',
  lastChecked: (monthYear: string) => `Last checked ${monthYear}`,
  linkSd: 'Service Descriptions (PDF)',
  linkHc: 'Help Center: service tiers',
  linkHcStatuses: 'Help Center: tier statuses',
  linkHcEditions: 'Help Center: country editions',
} as const;

/* --------------------------------- Data -------------------------------- */
type Tier = (typeof data.service_tiers)[number];
const TIERS: Tier[] = [...data.service_tiers].sort((a, b) => a.rank - b.rank);
const STARTER_MAX = data.editions.starter.max_users;
const STANDARD_MAX = data.editions.standard.max_users_typical;
const USERS_SLIDER_MAX = 150;
const USERS_FIELD_MAX = 10000;
const DOCS_SLIDER_MAX = 20000;
const LPD_SLIDER_MAX = 40;
const LINES_FIELD_MAX = 100000000;

const rule = (name: string) => data.rules_verbatim.find((r) => r.rule === name);
const RULE_UPGRADE = rule('upgrade_required_when_lines_or_users_exceed');
const RULE_STORAGE = rule('file_cabinet_storage_90_day_grace');
const RULE_NO_DOWNGRADE = rule('no_downgrade_during_term');
const RULE_AVERAGE = rule('six_month_rolling_average_definition');
const RULE_ABOVE_ULTIMATE = rule('above_ultimate_contact_sales');
const RULE_TABLE = rule('table_lead_in');

/* -------------------------------- Logic -------------------------------- */
export type EditionKey = 'starter' | 'standard' | 'premium_or_enterprise';
export interface EditionResult {
  key: EditionKey;
  /** Users allowed before the next edition family is required; null when Oracle publishes none. */
  nextLimit: number | null;
  headroom: number | null;
}

export function editionFor(users: number): EditionResult {
  if (users <= STARTER_MAX) return { key: 'starter', nextLimit: STARTER_MAX, headroom: STARTER_MAX - users };
  if (users <= STANDARD_MAX) return { key: 'standard', nextLimit: STANDARD_MAX, headroom: STANDARD_MAX - users };
  return { key: 'premium_or_enterprise', nextLimit: null, headroom: null };
}

export type Status = 'under' | 'near' | 'over';
export function statusFor(pct: number): Status {
  if (pct > 100) return 'over';
  if (pct >= 80) return 'near';
  return 'under';
}

export interface Metric { value: number; limit: number; pct: number; headroom: number; status: Status }
export type MetricKey = 'users' | 'lines';
export interface TierResult {
  /** First tier where both metrics fit; null when above Ultimate. */
  tier: Tier | null;
  /** Tier whose limits the bars are measured against (Ultimate when above every tier). */
  basis: Tier;
  users: Metric;
  lines: Metric;
  binding: MetricKey | 'tie';
  /** The tier directly below the result, and which metrics pushed past it. */
  ruledOut: { tier: Tier; by: MetricKey[] } | null;
}

export function tierFor(users: number, lines: number): TierResult {
  const fit = TIERS.find((t) => users <= t.users && lines <= t.lines) ?? null;
  const basis = fit ?? TIERS[TIERS.length - 1];
  const metric = (value: number, limit: number): Metric => {
    const pct = (value / limit) * 100;
    return { value, limit, pct, headroom: limit - value, status: statusFor(pct) };
  };
  const u = metric(users, basis.users);
  const l = metric(lines, basis.lines);
  const binding: MetricKey | 'tie' = u.pct === l.pct ? 'tie' : u.pct > l.pct ? 'users' : 'lines';
  const belowIdx = fit ? TIERS.indexOf(fit) - 1 : TIERS.length - 1;
  let ruledOut: TierResult['ruledOut'] = null;
  if (belowIdx >= 0) {
    const b = TIERS[belowIdx];
    const by: MetricKey[] = [];
    if (users > b.users) by.push('users');
    if (lines > b.lines) by.push('lines');
    ruledOut = { tier: b, by };
  }
  return { tier: fit, basis, users: u, lines: l, binding, ruledOut };
}

/* ------------------------------- Helpers ------------------------------- */
const fmt = (n: number): string => n.toLocaleString('en-US');
const fmtPct = (p: number): string => {
  if (p > 0 && p < 1) return '<1%';
  if (p > 100 && p < 101) return '>100%';
  const r = p >= 100 ? Math.round(p) : Math.round(p * 10) / 10;
  return `${fmt(r)}%`;
};
const clamp = (n: number, min: number, max: number): number => Math.min(max, Math.max(min, n));
const parseNum = (text: string, fallback: number, min: number, max: number): number => {
  const n = Number(text.replace(/[,\s]/g, ''));
  if (text.trim() === '' || !Number.isFinite(n)) return fallback;
  return clamp(Math.round(n), min, max);
};
const checkedMonthYear = (iso: string): string => {
  const [y, m] = iso.split('-').map(Number);
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${months[(m || 1) - 1]} ${y}`;
};
const HANDOFF_DEBOUNCE_MS = 300;

/* --------------------------- Scoped styles ----------------------------- */
const STYLE_ID = 'tc-styles';
const CSS = `
.tc-root { --tc-font: 'Poppins', system-ui, -apple-system, sans-serif;
  --ink: #1B2A33; --ink-2: #3E5460; --body: #5B6B75; --muted: #8A99A3;
  --line: #E4E7EA; --paper: #FFFFFF; --wash: #F5F8F8;
  --teal: #0f9993; --teal-deep: #0d555c; --teal-900: #04445c; --gold: #B08A2E; --red: #D92B03;
  position: relative; box-sizing: border-box; width: 100%; max-width: 860px;
  background: var(--paper); border: 1px solid var(--line); border-radius: 20px;
  box-shadow: 0 10px 20px -10px rgba(4,68,92,0.35);
  padding: 40px 44px 28px; color: var(--ink);
  font-family: var(--tc-font); -webkit-font-smoothing: antialiased; }
.tc-root *, .tc-root *::before, .tc-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
.tc-root p, .tc-root li { letter-spacing: normal; }
.tc-root ul { list-style: none; }
.tc-root .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
.tc-root .tc-logo { height: 40px; width: auto; flex: none; }
.tc-root.compact .tc-logo { display: none; }
.tc-root h2.ttl { font-size: 28px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.3; max-width: 560px; color: var(--ink); }
.tc-root h2.ttl .gold { color: var(--gold); display: block; }
.tc-root .sub { font-size: 14px; color: var(--body); margin-top: 10px; max-width: 680px; line-height: 1.55; }

.tc-root .inputs { margin-top: 28px; display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.tc-root .field { border: 1px solid var(--line); border-radius: 14px; padding: 18px 18px 16px; }
.tc-root .field > label.lbl, .tc-root .field .lbl { display: block; font-size: 14px; font-weight: 600; color: var(--ink-2); line-height: 1.35; }
.tc-root .hint { font-size: 12px; color: var(--muted); margin-top: 4px; line-height: 1.5; }
.tc-root .ctl { display: grid; grid-template-columns: 1fr 96px; gap: 12px; align-items: center; margin-top: 12px; }
.tc-root .ctl.narrow { grid-template-columns: 1fr 84px; }
.tc-root input[type=range] { -webkit-appearance: none; appearance: none; width: 100%; height: 28px; background: transparent; cursor: pointer; }
.tc-root input[type=range]:focus { outline: none; }
.tc-root input[type=range]::-webkit-slider-runnable-track { height: 6px; border-radius: 3px; background: linear-gradient(90deg, var(--teal) var(--fill, 0%), var(--line) var(--fill, 0%)); }
.tc-root input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2px solid var(--teal); margin-top: -7px; box-shadow: 0 1px 4px rgba(4,68,92,0.25); transition: box-shadow .15s ease; }
.tc-root input[type=range]:focus-visible::-webkit-slider-thumb { box-shadow: 0 0 0 4px rgba(15,153,147,0.28); }
.tc-root input[type=range]::-moz-range-track { height: 6px; border-radius: 3px; background: var(--line); }
.tc-root input[type=range]::-moz-range-progress { height: 6px; border-radius: 3px; background: var(--teal); }
.tc-root input[type=range]::-moz-range-thumb { width: 16px; height: 16px; border-radius: 50%; background: #fff; border: 2px solid var(--teal); box-shadow: 0 1px 4px rgba(4,68,92,0.25); }
.tc-root input[type=range]:focus-visible::-moz-range-thumb { box-shadow: 0 0 0 4px rgba(15,153,147,0.28); }
.tc-root .num { width: 100%; height: 40px; border: 1px solid var(--line); border-radius: 10px; padding: 0 10px; font: inherit; font-size: 15px; font-weight: 600; color: var(--ink); text-align: right; background: #fff; transition: border-color .15s ease, box-shadow .15s ease; -moz-appearance: textfield; appearance: textfield; }
.tc-root .num::-webkit-outer-spin-button, .tc-root .num::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.tc-root .num:focus { outline: none; border-color: var(--teal); box-shadow: 0 0 0 3px rgba(15,153,147,0.2); }
.tc-root .num.wide { text-align: right; }
.tc-root .modes { display: flex; gap: 6px; margin-top: 10px; padding: 3px; background: var(--wash); border-radius: 10px; }
.tc-root .mode { flex: 1; font: inherit; font-size: 12.5px; font-weight: 500; color: var(--ink-2); background: transparent; border: 0; border-radius: 8px; padding: 7px 8px; cursor: pointer; transition: background .15s ease, color .15s ease; }
.tc-root .mode[aria-checked="true"] { background: #fff; color: var(--teal-deep); font-weight: 600; box-shadow: 0 1px 3px rgba(4,68,92,0.15); }
.tc-root .mode:focus-visible { outline: 2px solid var(--teal); outline-offset: 1px; }
.tc-root .sublbl { display: block; font-size: 12.5px; font-weight: 500; color: var(--ink-2); margin-top: 12px; }
.tc-root .product { margin-top: 12px; padding-top: 10px; border-top: 1px dashed var(--line); font-size: 13px; color: var(--body); display: flex; flex-wrap: wrap; gap: 4px 8px; align-items: baseline; }
.tc-root .product b { font-size: 17px; font-weight: 600; color: var(--ink); }

.tc-root .results { margin-top: 18px; display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.tc-root .panel { background: var(--wash); border-radius: 14px; padding: 20px 20px 18px; position: relative; }
.tc-root .eyebrow { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.tc-root .eyebrow b { font-size: 11px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--teal-deep); }
.tc-root .eyebrow span { font-size: 11.5px; color: var(--muted); }
.tc-root .big { margin-top: 10px; font-size: 26px; font-weight: 600; line-height: 1.2; color: var(--teal-900); transition: color .25s ease; }
.tc-root .big.open { color: var(--gold); }
.tc-root .detail { margin-top: 6px; font-size: 13px; color: var(--body); line-height: 1.5; }
.tc-root .dist { margin-top: 12px; font-size: 13.5px; font-weight: 500; color: var(--ink); line-height: 1.5; }
.tc-root .fn { margin-top: 14px; padding-top: 10px; border-top: 1px solid var(--line); font-size: 11.5px; color: var(--muted); line-height: 1.55; }

.tc-root .meters { margin-top: 14px; display: flex; flex-direction: column; gap: 12px; }
.tc-root .meter { position: relative; }
.tc-root .mrow { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; flex-wrap: wrap; font-size: 12.5px; color: var(--ink-2); }
.tc-root .mrow .mname { font-weight: 600; color: var(--ink); }
.tc-root .mrow .mval { color: var(--body); }
.tc-root .track { margin-top: 6px; height: 10px; background: #fff; border: 1px solid var(--line); border-radius: 6px; overflow: hidden; }
.tc-root .bar { height: 100%; border-radius: 6px; width: 0; background: var(--teal); transition: width .45s cubic-bezier(0.19,1,0.22,1), background .25s ease; }
.tc-root .bar.near { background: var(--gold); }
.tc-root .bar.over { background: var(--red); }
.tc-root .pill { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; background: rgba(15,153,147,0.12); color: var(--teal-deep); transition: background .25s ease, color .25s ease; }
.tc-root .pill::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.tc-root .pill.near { background: rgba(176,138,46,0.14); color: #7A5F1D; }
.tc-root .pill.over { background: rgba(217,43,3,0.12); color: var(--red); }
.tc-root .pct { font-weight: 600; color: var(--ink); min-width: 3.2em; text-align: right; }
.tc-root .bind { margin-top: 12px; font-size: 13.5px; font-weight: 500; color: var(--ink); line-height: 1.5; }
.tc-root .why { margin-top: 4px; font-size: 12.5px; color: var(--body); line-height: 1.5; }

.tc-root .country { grid-column: 1 / -1; background: var(--paper); border: 1px dashed rgba(176,138,46,0.55); border-left: 4px solid var(--gold); padding: 16px 20px 14px; }
.tc-root .country .eyebrow b { color: #7A5F1D; }
.tc-root .country .crow { display: grid; grid-template-columns: 180px minmax(0, 1fr); gap: 6px 22px; align-items: start; margin-top: 8px; }
.tc-root .country .cname { font-size: 19px; font-weight: 600; line-height: 1.25; color: var(--ink); }
.tc-root .country .cnot { margin-top: 4px; font-size: 11.5px; color: var(--muted); line-height: 1.5; }
.tc-root .country .clist { display: flex; flex-direction: column; gap: 4px; font-size: 12.5px; color: var(--body); line-height: 1.5; }
.tc-root .country p { font-family: var(--tc-font); margin: 0; }
.tc-root .country .clist p { font-size: 12.5px; line-height: 1.5; color: var(--body); }
.tc-root .country .clist p:first-child { color: var(--ink); font-weight: 500; }
.tc-root .country .csrc { position: relative; margin-top: 10px; font-size: 11px; color: var(--muted); line-height: 1.5; }
.tc-root .country .csrc a { color: var(--teal-deep); text-decoration: underline; text-underline-offset: 2px; }
.tc-root .country .csrc a:focus-visible { outline: 2px solid var(--teal); outline-offset: 2px; border-radius: 2px; }
.tc-root .hint { position: relative; }

.tc-root .helpers { margin-top: 22px; }
.tc-root .helpers h3 { font-size: 12px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: var(--muted); }
.tc-root .helpers ul { margin-top: 10px; display: flex; flex-direction: column; gap: 8px; }
.tc-root .helpers li { position: relative; padding-left: 16px; font-size: 13px; color: var(--body); line-height: 1.55; }
.tc-root .helpers li::before { content: ''; position: absolute; left: 0; top: 0.62em; width: 6px; height: 6px; border-radius: 50%; background: var(--teal); }

.tc-root .tt { display: inline; position: static; }
.tc-root .tt-btn { display: inline; font: inherit; font-size: 0.92em; color: var(--teal); background: none; border: 0; padding: 0; margin-left: 4px; cursor: pointer; text-decoration: underline dotted; text-underline-offset: 3px; }
.tc-root .tt-btn:focus-visible { outline: 2px solid var(--teal); outline-offset: 2px; border-radius: 3px; }
.tc-root .tt-box { position: absolute; left: 0; right: 0; top: calc(100% + 6px); z-index: 6; background: var(--ink); color: #fff; padding: 10px 13px; border-radius: 10px; font-size: 12.5px; line-height: 1.55; opacity: 0; visibility: hidden; transform: translateY(-4px); transition: opacity .15s ease, transform .15s ease, visibility 0s linear .15s; box-shadow: 0 12px 30px -12px rgba(0,0,0,0.5); font-weight: 400; text-transform: none; letter-spacing: normal; text-align: left; }
.tc-root .tt-box .t-src { display: block; margin-top: 6px; color: #A9D9D4; font-size: 11px; }
.tc-root .tt:hover .tt-box, .tc-root .tt.open .tt-box, .tc-root .tt-btn:focus-visible + .tt-box { opacity: 1; visibility: visible; transform: translateY(0); transition-delay: 0s; }
.tc-root .eyebrow, .tc-root .detail, .tc-root .why { position: relative; }

.tc-root .foot { margin-top: 24px; padding-top: 14px; border-top: 1px solid var(--line); }
.tc-root .src { font-size: 11px; color: var(--muted); line-height: 1.6; }
.tc-root .stamp { display: inline-block; margin-right: 8px; font-size: 10.5px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--teal-deep); background: rgba(15,153,147,0.1); border-radius: 999px; padding: 2px 9px; vertical-align: 1px; }
.tc-root .links { margin-top: 6px; display: flex; flex-wrap: wrap; gap: 6px 16px; font-size: 11px; }
.tc-root .links a { color: var(--teal-deep); text-decoration: underline; text-underline-offset: 2px; }
.tc-root .links a:hover { color: var(--teal); }
.tc-root .links a:focus-visible { outline: 2px solid var(--teal); outline-offset: 2px; border-radius: 2px; }

.tc-root.compact { padding: 24px 16px 20px; border-radius: 14px; }
.tc-root.compact h2.ttl { font-size: 22px; }
.tc-root.compact .sub { font-size: 13px; }
.tc-root.compact .inputs, .tc-root.compact .results { grid-template-columns: 1fr; gap: 12px; margin-top: 20px; }
.tc-root.compact .results { margin-top: 12px; }
.tc-root.compact .field { padding: 14px; }
.tc-root.compact .panel { padding: 16px 14px; }
.tc-root.compact .big { font-size: 22px; }
.tc-root.compact .country { padding: 14px 14px 12px; }
.tc-root.compact .country .crow { grid-template-columns: 1fr; }
.tc-root.compact .ctl { grid-template-columns: 1fr 88px; gap: 10px; }
.tc-root.compact .ctl.narrow { grid-template-columns: 1fr 76px; }
@media (prefers-reduced-motion: reduce) {
  .tc-root .bar, .tc-root .tt-box, .tc-root .big, .tc-root .pill { transition: none; }
}`;

function useInjectedStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID;
      s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

/* ------------------------------ Sub-parts ------------------------------ */
interface TipProps { label?: string; source?: string; children: React.ReactNode }

/** Hover, focus or tap reveals verbatim source wording. Anchored to the nearest positioned ancestor. */
const Tip: React.FC<TipProps> = ({ label = STR.sourceTip, source, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className={`tt${open ? ' open' : ''}`}>
      <button
        type="button"
        className="tt-btn"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); (e.currentTarget as HTMLButtonElement).blur(); } }}
      >
        {label}
      </button>
      <span className="tt-box" role="tooltip">
        {children}
        {source && <span className="t-src">{source}</span>}
      </span>
    </span>
  );
};

interface MeterProps { name: string; m: Metric; statusTip: React.ReactNode }

const Meter: React.FC<MeterProps> = ({ name, m, statusTip }) => (
  <div className="meter">
    <div className="mrow">
      <span>
        <span className="mname">{name}</span>{' '}
        <span className="mval">{STR.ofLimit(fmt(m.value), fmt(m.limit))}</span>
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
        <span className={`pill ${m.status}`}>{STR.status[m.status]}</span>
        <span className="pct">{fmtPct(m.pct)}</span>
      </span>
    </div>
    <div className="track" role="progressbar" aria-label={name} aria-valuemin={0} aria-valuemax={m.limit} aria-valuenow={Math.min(m.value, m.limit)} aria-valuetext={`${fmt(m.value)} of ${fmt(m.limit)}, ${fmtPct(m.pct)}, ${STR.status[m.status]}`}>
      <div className={`bar ${m.status}`} style={{ width: `${Math.min(100, m.pct)}%` }} />
    </div>
    <div className="mrow" style={{ marginTop: 4, fontSize: 11.5 }}>
      <span className="mval">{statusTip}</span>
    </div>
  </div>
);

/* ------------------------------ Component ------------------------------ */
export interface TierCheckerProps { compact?: boolean }

const TierChecker: React.FC<TierCheckerProps> = ({ compact: compactProp }) => {
  useInjectedStyles();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [measuredCompact, setMeasuredCompact] = useState(false);
  const compact = compactProp ?? measuredCompact;
  const uid = 'tc';

  useEffect(() => {
    const el = rootRef.current;
    if (!el || compactProp !== undefined) return;
    const measure = (): void => setMeasuredCompact(el.offsetWidth < 640);
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, [compactProp]);

  // Text state so people can clear a field while typing; numbers are derived with a fallback.
  const [usersText, setUsersText] = useState('12');
  const [docsText, setDocsText] = useState('1500');
  const [lpdText, setLpdText] = useState('6');
  const [directText, setDirectText] = useState('9000');
  const [mode, setMode] = useState<'product' | 'direct'>('product');

  const users = parseNum(usersText, 1, 1, USERS_FIELD_MAX);
  const docs = parseNum(docsText, 0, 0, LINES_FIELD_MAX);
  const lpd = parseNum(lpdText, 1, 1, 10000);
  const product = Math.min(LINES_FIELD_MAX, docs * lpd);
  const direct = parseNum(directText, 0, 0, LINES_FIELD_MAX);
  const lines = mode === 'product' ? product : direct;

  // Publish the reader's inputs for the other pricing widgets, but only once they've touched something.
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched) return;
    const t = window.setTimeout(() => writeHandoff({ users, linesPerMonth: lines, source: 'tier-checker' }), HANDOFF_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [touched, users, lines]);
  const pickMode = (m: 'product' | 'direct'): void => { setMode(m); setTouched(true); };

  const edition = useMemo(() => editionFor(users), [users]);
  const tier = useMemo(() => tierFor(users, lines), [users, lines]);

  const normalise = (setter: (s: string) => void, fallback: number, min: number, max: number) =>
    (e: React.FocusEvent<HTMLInputElement>) => setter(String(parseNum(e.target.value, fallback, min, max)));

  const sliderFill = (v: number, min: number, max: number): string => `${(((v - min) / (max - min)) * 100).toFixed(2)}%`;

  const editionStr = STR.edition[edition.key];
  const editionTip = edition.key === 'starter' ? STR.editionTipStarter : edition.key === 'standard' ? STR.editionTipStandard : STR.editionTipPremium;
  const editionPage = edition.key === 'starter' ? data.editions.starter.page : data.editions.standard.page;
  const nextEdition = edition.key === 'starter' ? STR.editionNextStandard : STR.editionNextPremium;

  const statusInfo = (s: Status) => data.statuses.find((x) => x.indicator === STR.status[s]);
  const statusTip = (m: Metric): React.ReactNode => {
    const info = statusInfo(m.status);
    return (
      <Tip source={STR.srcHc}>
        {info ? `${info.indicator}: ${info.trigger_verbatim} ${info.description_verbatim}` : STR.status[m.status]}
      </Tip>
    );
  };

  const nextTier = tier.tier ? TIERS.find((t) => t.rank === tier.tier!.rank + 1) ?? null : null;
  const tierBadge = tier.tier ? `${tier.tier.tier} ${STR.tierSuffix}` : STR.aboveUltimate;
  const badgeStatus: Status = tier.tier ? (tier.users.pct >= tier.lines.pct ? tier.users.status : tier.lines.status) : 'over';

  const byLabel = (by: MetricKey[]): string => (by.length === 2 ? STR.byBoth : by[0] === 'users' ? STR.byUsers : STR.byLines);
  const limitLabel = (r: NonNullable<TierResult['ruledOut']>): string =>
    r.by.length === 2
      ? STR.limitBoth(fmt(r.tier.users), fmt(r.tier.lines))
      : r.by[0] === 'users' ? STR.limitUsers(fmt(r.tier.users)) : STR.limitLines(fmt(r.tier.lines));

  const idUsers = `${uid}-users`;
  const idDocs = `${uid}-docs`;
  const idLpd = `${uid}-lpd`;
  const idDirect = `${uid}-direct`;

  return (
    <div className={`tc-root${compact ? ' compact' : ''}`} ref={rootRef}>
      <div className="head">
        <h2 className="ttl">{STR.title} <span className="gold">{STR.titleAccent}</span></h2>
        <img className="tc-logo" src={PSGLOBAL_LOGO} alt={STR.logoAlt} />
      </div>
      <p className="sub">{STR.sub}</p>

      {/* ------------------------------ Inputs ------------------------------ */}
      <div className="inputs" onChange={() => setTouched(true)}>
        <div className="field">
          <label className="lbl" htmlFor={idUsers}>{STR.usersLabel}</label>
          <p className="hint">
            {STR.usersHint}
            <Tip label={STR.usersHintTipLabel} source={STR.srcSdGlossary}>{STR.usersHintTip}</Tip>
          </p>
          <div className="ctl">
            <input
              type="range"
              min={1}
              max={USERS_SLIDER_MAX}
              step={1}
              value={clamp(users, 1, USERS_SLIDER_MAX)}
              aria-label={STR.usersLabel}
              style={{ ['--fill' as string]: sliderFill(clamp(users, 1, USERS_SLIDER_MAX), 1, USERS_SLIDER_MAX) }}
              onChange={(e) => setUsersText(e.target.value)}
            />
            <input
              id={idUsers}
              className="num"
              type="number"
              inputMode="numeric"
              min={1}
              max={USERS_FIELD_MAX}
              value={usersText}
              onChange={(e) => setUsersText(e.target.value)}
              onBlur={normalise(setUsersText, 1, 1, USERS_FIELD_MAX)}
            />
          </div>
        </div>

        <div className="field">
          <span className="lbl">{STR.linesLabel}</span>
          <div className="modes" role="radiogroup" aria-label={STR.linesLabel}>
            <button type="button" className="mode" role="radio" aria-checked={mode === 'product'} onClick={() => pickMode('product')}>{STR.modeProduct}</button>
            <button type="button" className="mode" role="radio" aria-checked={mode === 'direct'} onClick={() => pickMode('direct')}>{STR.modeDirect}</button>
          </div>

          {mode === 'product' ? (
            <>
              <label className="sublbl" htmlFor={idDocs}>{STR.docsLabel}</label>
              <div className="ctl">
                <input
                  type="range"
                  min={0}
                  max={DOCS_SLIDER_MAX}
                  step={50}
                  value={clamp(docs, 0, DOCS_SLIDER_MAX)}
                  aria-label={STR.docsLabel}
                  style={{ ['--fill' as string]: sliderFill(clamp(docs, 0, DOCS_SLIDER_MAX), 0, DOCS_SLIDER_MAX) }}
                  onChange={(e) => setDocsText(e.target.value)}
                />
                <input id={idDocs} className="num" type="number" inputMode="numeric" min={0} value={docsText}
                  onChange={(e) => setDocsText(e.target.value)} onBlur={normalise(setDocsText, 0, 0, LINES_FIELD_MAX)} />
              </div>
              <label className="sublbl" htmlFor={idLpd}>{STR.lpdLabel}</label>
              <div className="ctl narrow">
                <input
                  type="range"
                  min={1}
                  max={LPD_SLIDER_MAX}
                  step={1}
                  value={clamp(lpd, 1, LPD_SLIDER_MAX)}
                  aria-label={STR.lpdLabel}
                  style={{ ['--fill' as string]: sliderFill(clamp(lpd, 1, LPD_SLIDER_MAX), 1, LPD_SLIDER_MAX) }}
                  onChange={(e) => setLpdText(e.target.value)}
                />
                <input id={idLpd} className="num" type="number" inputMode="numeric" min={1} value={lpdText}
                  onChange={(e) => setLpdText(e.target.value)} onBlur={normalise(setLpdText, 1, 1, 10000)} />
              </div>
              <p className="product" aria-live="polite">
                <span>{fmt(docs)} {STR.times} {fmt(lpd)} {STR.equals}</span> <b>{fmt(product)}</b> <span>{STR.productLabel.toLowerCase()}</span>
                <span className={`pill ${badgeStatus}`} aria-label={STR.badgeAria(tierBadge)}>{tierBadge}</span>
              </p>
            </>
          ) : (
            <>
              <label className="sublbl" htmlFor={idDirect}>{STR.directLabel}</label>
              <div className="ctl" style={{ gridTemplateColumns: '1fr' }}>
                <input id={idDirect} className="num wide" type="number" inputMode="numeric" min={0} value={directText}
                  onChange={(e) => setDirectText(e.target.value)} onBlur={normalise(setDirectText, 0, 0, LINES_FIELD_MAX)} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ------------------------------ Results ----------------------------- */}
      <div className="results" aria-live="polite">
        <section className="panel" aria-label={STR.editionEyebrow}>
          <div className="eyebrow">
            <b>{STR.editionEyebrow}</b>
            <span>
              {STR.editionBasis}
              <Tip source={STR.srcSd(editionPage)}>{editionTip}</Tip>
            </span>
          </div>
          <div className={`big${edition.key === 'premium_or_enterprise' ? ' open' : ''}`}>{editionStr.name}</div>
          <p className="detail">{editionStr.detail}</p>
          <p className="dist">
            {edition.headroom === null ? STR.editionOpen : STR.editionDistance(edition.headroom, nextEdition)}
          </p>
          <p className="fn">{STR.editionFootnote}</p>
        </section>

        <section className="panel" aria-label={STR.tierEyebrow}>
          <div className="eyebrow">
            <b>{STR.tierEyebrow}</b>
            <span>
              {STR.tierBasis}
              <Tip source={STR.srcSd(RULE_UPGRADE?.page ?? 106)}>
                {RULE_TABLE?.verbatim} {RULE_UPGRADE?.verbatim}
              </Tip>
            </span>
          </div>
          {tier.tier ? (
            <>
              <div className="big">{tier.tier.tier} {STR.tierSuffix}</div>
              <p className="detail">{STR.tierDetail(fmt(tier.tier.users), fmt(tier.tier.lines))}</p>
              <p className="dist">
                {STR.tierDistance(
                  Math.max(0, tier.users.headroom),
                  fmt(Math.max(0, tier.lines.headroom)),
                  nextTier ? STR.tierNextName(nextTier.tier) : STR.tierTopName,
                )}
              </p>
            </>
          ) : (
            <>
              <div className="big open">{STR.aboveUltimate}</div>
              <p className="detail">
                {STR.aboveUltimateDetail}
                <Tip source={STR.srcSd(RULE_ABOVE_ULTIMATE?.page ?? 106)}>{RULE_ABOVE_ULTIMATE?.verbatim}</Tip>
              </p>
              <p className="detail">{STR.measuredAgainst(tier.basis.tier)}</p>
            </>
          )}

          <div className="meters">
            <Meter name={STR.metricUsers} m={tier.users} statusTip={statusTip(tier.users)} />
            <Meter name={STR.metricLines} m={tier.lines} statusTip={statusTip(tier.lines)} />
          </div>

          <p className="bind">{STR.binding[tier.binding]}</p>
          {tier.ruledOut && tier.ruledOut.by.length > 0 && (
            <p className="why">
              {STR.ruledOut(tier.ruledOut.tier.tier, byLabel(tier.ruledOut.by), limitLabel(tier.ruledOut))}
            </p>
          )}
          {(tier.tier === null || tier.tier.rank > 1) && (
            <p className="why">
              {STR.tierUpgradeNote}
              <Tip source={STR.srcSd(RULE_NO_DOWNGRADE?.page ?? 106)}>{RULE_NO_DOWNGRADE?.verbatim}</Tip>
            </p>
          )}
        </section>

        <section className="panel country" aria-label={STR.countryEyebrow}>
          <div className="eyebrow">
            <b>{STR.countryEyebrow}</b>
            <span>{STR.countryBasis}</span>
          </div>
          <div className="crow">
            <div>
              <div className="cname">{STR.countryName}</div>
              <p className="cnot">{STR.countryNotTier}</p>
            </div>
            <div className="clist">
              <p>{STR.countryLead}</p>
              <p>{STR.countryLocalised(data.country_edition.localized_editions.join(', '))} {STR.countryTax}</p>
              <p>{STR.countryOneWorld}</p>
            </div>
          </div>
          <p className="csrc">
            {STR.countrySrcLabel}{' '}
            <a href={data.country_edition.url} target="_blank" rel="noopener noreferrer">{STR.countrySrc}</a>
            <Tip source={STR.countrySrc}>&ldquo;{STR.countryTip}&rdquo;</Tip>
          </p>
        </section>
      </div>

      {/* ------------------------------ Helpers ----------------------------- */}
      <div className="helpers">
        <h3>{STR.helpersTitle}</h3>
        <ul>
          <li>
            {STR.helpers.counting}
            <Tip source={STR.srcHc}>{STR.countingTip}</Tip>
          </li>
          <li>
            {STR.helpers.average}
            <Tip source={STR.srcSd(RULE_AVERAGE?.page ?? 7)}>{RULE_AVERAGE?.verbatim}</Tip>
          </li>
          <li>
            {STR.helpers.noDowngrade}
            <Tip source={STR.srcSd(RULE_NO_DOWNGRADE?.page ?? 106)}>{RULE_UPGRADE?.verbatim} {RULE_NO_DOWNGRADE?.verbatim}</Tip>
          </li>
          <li>
            {STR.helpers.storage}
            <Tip source={STR.srcSd(RULE_STORAGE?.page ?? 106)}>{RULE_STORAGE?.verbatim}</Tip>
          </li>
          <li>
            {STR.helpers.dashboard}
            <Tip source={STR.srcHc}>{STR.dashboardTip}</Tip>
          </li>
        </ul>
      </div>

      {/* ------------------------------- Footer ----------------------------- */}
      <div className="foot">
        <p className="src">
          <span className="stamp">{STR.lastChecked(checkedMonthYear(data.meta.checked))}</span>
          {STR.footer}
        </p>
        <p className="links">
          <a href={data.meta.source_url} target="_blank" rel="noopener noreferrer">{STR.linkSd}</a>
          <a href={data.meta.help_center_tiers_url} target="_blank" rel="noopener noreferrer">{STR.linkHc}</a>
          <a href={data.meta.help_center_statuses_url} target="_blank" rel="noopener noreferrer">{STR.linkHcStatuses}</a>
          <a href={data.meta.help_center_editions_url} target="_blank" rel="noopener noreferrer">{STR.linkHcEditions}</a>
        </p>
      </div>
    </div>
  );
};

export default TierChecker;

/* ------------------------------ Test cases ------------------------------
 * Boundaries are inclusive ("up to N" fits; an upgrade is required only when
 * exceeded). Percentages are value / limit of the RESULT tier.
 *
 * | U     | L          | Edition               | Edition headroom              | Tier                | Users % / status | Lines % / status | Binding |
 * |-------|------------|-----------------------|-------------------------------|---------------------|------------------|------------------|---------|
 * | 1     | 0          | Starter               | 9 more before Standard        | Standard            | 1% under         | 0% under         | users   |
 * | 10    | 50,000     | Starter               | 0 (at the limit)              | Standard            | 10% under        | 25% under        | lines   |
 * | 11    | 50,000     | Standard              | 19 more before Prem./Ent.     | Standard            | 11% under        | 25% under        | lines   |
 * | 30    | 100,000    | Standard              | 0 (at the limit)              | Standard            | 30% under        | 50% under        | lines   |
 * | 31    | 100,000    | Premium or Enterprise | none published                | Standard            | 31% under        | 50% under        | lines   |
 * | 40    | 250,000    | Premium or Enterprise | none published                | Premium (lines bind)| 4% under         | 12.5% under      | lines   |
 * | 90    | 170,000    | Premium or Enterprise | none published                | Standard            | 90% near         | 85% near         | users   |
 * | 100   | 200,000    | Premium or Enterprise | none published                | Standard            | 100% near        | 100% near        | tie     |
 * | 101   | 1,000      | Premium or Enterprise | none published                | Premium (users bind)| 10.1% under      | <1% under        | users   |
 * | 150   | 2,000,001  | Premium or Enterprise | none published                | Enterprise          | 7.5% under       | 20% under        | lines   |
 * | 2,001 | 1,000      | Premium or Enterprise | none published                | Ultimate            | 50% under        | <1% under        | users   |
 * | 4,001 | 1,000      | Premium or Enterprise | none published                | Above Ultimate      | >100% over       | <1% under        | users   |
 * | 10    | 60,000,000 | Starter               | 0 (at the limit)              | Above Ultimate      | <1% under        | 120% over        | lines   |
 * ------------------------------------------------------------------------ */
